import React from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import * as qs from 'query-string';
import { withAccount } from '../../components/AccountContext';
import { withGrowl } from '../../components/GrowlNotifications';
import {
  locationShape, intlShape, historyShape, growlShape,
} from '../../components/prop-types';
import Debug from '../../components/Debug';
import { notificationMessages } from '../../i18n/common';
import { TextAreaWithValidation } from '../../components/prime-formik';

const DECLINE_INVITATION = gql`
  mutation declineJoinAccountInvitation($input: DeclineJoinAccountInvitationInput!) {
    declineJoinAccountInvitation(input: $input) {
      clientMutationId
    }
  }
`;

const messages = defineMessages({
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
});

function UserInvitationDeclineForm(props) {
  const { intl: { formatMessage }, location, history } = props;
  const { token } = qs.parse(location.search);

  return (
    <Form>
      <input type="hidden" name="token" value={token} />
      <div className="p-grid">
        <div className="p-grid p-col-6">
          <div className="p-col-12">
            <Field
              id="message"
              name="message"
              labelText="Message"
              component={TextAreaWithValidation}
            />
            <Button
              type="button"
              label={formatMessage(messages.cancel)}
              onClick={() => history.push(`/register?token=${token}`)}
            />
            <Button type="submit" label={formatMessage(messages.submit)} />
          </div>
        </div>
      </div>
      <Debug />
    </Form>
  );
}

UserInvitationDeclineForm.propTypes = {
  location: locationShape.isRequired,
  history: historyShape.isRequired,
  intl: intlShape.isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    declineInvitationMutation,
    growl,
    intl: { formatMessage },
    history,
  } = props;

  const {
    token,
    message,
  } = values;

  try {
    await declineInvitationMutation({
      variables: {
        input: {
          token,
          message,
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
    history.push('/');
  } catch (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }
}

function buildValidationSchema() {
  return Yup.object().shape({
    message: Yup.string()
      .default(''),
  });
}

function mapPropsToValues(props) {
  const validationSchema = buildValidationSchema();

  const { location } = props;
  const { token } = qs.parse(location.search);

  const initialValues = {
    ...validationSchema.cast(),
    token,
  };
  return initialValues;
}

const UserInvitationDeclineFormEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(UserInvitationDeclineForm);

function UserInvitationDecline(props) {
  const [declineClientInvitationMutation] = useMutation(DECLINE_INVITATION, {
  });

  return (
    <div className="p-grid p-justify-center">
      <div className="p-sm-12 p-md-10 p-lg-8">
        <Panel>
          <FormattedMessage
            id="decline-account-invitation.form.body-text"
            // eslint-disable-next-line max-len
            defaultMessage="Please give us a reason why you do not want to go through with this invitation. Your feedback is much appreciated."
            tagName="h3"
          />
          <UserInvitationDeclineFormEnhancedForm
            declineInvitationMutation={declineClientInvitationMutation}
            {...props}
          />
        </Panel>
      </div>
    </div>
  );
}

UserInvitationDecline.propTypes = {
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  growl: growlShape.isRequired,
};

export default withAccount(injectIntl(withRouter(withGrowl(UserInvitationDecline))));
